// OWNER: FRONTEND-ADMIN. Column widths for the dense admin tables (Numbers, Reports funnel). SPEC §15: "works at
// 1280×720". These tables render a <colgroup> from the specs below with `table-layout: fixed` (admin.css .adm-fixed),
// so the widths here ARE the rendered widths: a table can't grow past its budget and push columns off-screen.
// tests/admin-layout.test.ts checks every spec against the budget and checks the shell numbers against styles.css.
import type { FunnelRow } from '../../../src/shared/api';

// ---------- the space a table gets at a given viewport ----------

/** App shell in web/src/styles.css: `.shell` nav column 200 px, `.main` padding 24 px left and right. */
export const SHELL_NAV_PX = 200;
export const MAIN_PAD_X = 24;
/** A classic vertical scrollbar (Windows, when the page is taller than the window). Overlay scrollbars take 0. */
export const SCROLLBAR_PX = 15;
/** `.adm-table-wrap` has a 1 px border on each side. */
export const TABLE_WRAP_BORDER_PX = 2;
/** `.card`: 16 px padding + 1 px border, each side. */
export const CARD_INSET_PX = 34;
/** `.adm-fixed th, .adm-fixed td` horizontal padding (admin.css). Column widths include it. */
export const CELL_PAD_X = 8;
/** SPEC §15 target. */
export const TARGET_VIEWPORT_PX = 1280;

/** Width a table inside `.adm-table-wrap` gets on an admin page, optionally inside a `.card`. */
export function tableBudget(viewportPx: number, inCard: boolean): number {
  return viewportPx - SCROLLBAR_PX - SHELL_NAV_PX - 2 * MAIN_PAD_X - TABLE_WRAP_BORDER_PX - (inCard ? CARD_INSET_PX : 0);
}

export interface ColumnSpec<K extends string = string> {
  key: K;
  label: string;          // header text (uppercased by CSS); may wrap at spaces
  width: number;          // px, padding included (table-layout: fixed)
  num?: boolean;          // right-aligned tabular numbers
  title?: string;         // header tooltip
  srLabel?: string;       // screen-reader header when the visible label is empty
}

export const columnsWidth = (cols: ReadonlyArray<ColumnSpec>): number => cols.reduce((a, c) => a + c.width, 0);

/** Header text is 11 px IBM Plex Mono, uppercase, 0.06em tracking: about 7.3 px per character. */
export const HEADER_CHAR_PX = 7.3;
/** A header wraps only at spaces, so its longest word (plus cell padding) must fit the column. */
export function headerMinWidth(label: string): number {
  const longest = Math.max(0, ...label.split(/\s+/).map((w) => w.length));
  return Math.ceil(longest * HEADER_CHAR_PX) + 2 * CELL_PAD_X;
}

// ---------- Numbers (SPEC §15.7) ----------

export type NumberColumnKey =
  | 'number' | 'metro' | 'status' | 'today' | 'connect' | 'baseline' | 'peers' | 'short' | 'sip' | 'registered' | 'actions';

/** Ramp day sits under today's dials, dials-in-window under the connect rate, and the three registrations plus the
 *  STIR/SHAKEN attestation share one column (one line each, with dates; 'STIR A Sep 24' fits, a failure wraps its date).
 *  Status actions live in the row's Manage menu and in the ledger. */
export const NUMBER_COLUMNS: ReadonlyArray<ColumnSpec<NumberColumnKey>> = [
  { key: 'number', label: 'Number', width: 148 },
  { key: 'metro', label: 'Metro', width: 112 },
  { key: 'status', label: 'Status', width: 96 },
  { key: 'today', label: 'Today', width: 80, num: true, title: "Dials placed today / today's cap. Below: ramp day (Toronto calendar days)." },
  { key: 'connect', label: 'Connect 3d', width: 80, num: true, title: 'Connects ÷ dials over the 3-day health window. Below: dials in the window.' },
  { key: 'baseline', label: 'Baseline', width: 76, num: true, title: "This number's own trailing 14 days before the window" },
  { key: 'peers', label: 'Peers', width: 64, num: true, title: 'Median of same-country active numbers' },
  { key: 'short', label: 'Short', width: 60, num: true, title: 'Answered, under 15 s, no human disposition' },
  { key: 'sip', label: '603/608', width: 72, num: true, title: 'Carrier rejections in the window: SIP 603 (decline) / 608 (rejected)' },
  { key: 'registered', label: 'Caller ID', width: 123, title: 'CNAM (US only), Free Caller Registry, Hiya, and the STIR/SHAKEN attestation of the last self-test call (green A, amber B/C, red none or failed)' },
  { key: 'actions', label: '', srLabel: 'Actions', width: 104 },
];

// ---------- Reports funnel (SPEC §15.6) ----------

export type FunnelGroupKey = 'week' | 'campaign' | 'user';
export type FunnelColumnKey =
  | FunnelGroupKey | 'range' | 'dials' | 'connects' | 'dm' | 'booked' | 'held' | 'dialsPerMeeting' | 'vmLive' | 'vmDropped' | 'talk';

export interface FunnelColumn extends ColumnSpec<FunnelColumnKey> {
  shows: ReadonlyArray<keyof FunnelRow>;   // the FunnelRow fields this column displays (rates sit under their counts)
}

const FUNNEL_GROUP_COLUMNS: Record<FunnelGroupKey | 'range', FunnelColumn> = {
  week: { key: 'week', label: 'Week', width: 84, shows: ['week'] },
  campaign: { key: 'campaign', label: 'Campaign', width: 160, shows: ['campaignName'] },
  user: { key: 'user', label: 'Setter', width: 112, shows: ['userName'] },
  range: { key: 'range', label: 'Range', width: 180, shows: [] },
};

export const FUNNEL_METRIC_COLUMNS: ReadonlyArray<FunnelColumn> = [
  { key: 'dials', label: 'Dials', width: 66, num: true, shows: ['dials'] },
  { key: 'connects', label: 'Connects', width: 76, num: true, title: 'Below: connect rate (connects ÷ dials)', shows: ['connects', 'connectRate'] },
  { key: 'dm', label: 'DM convos', width: 66, num: true, shows: ['dmConversations'] },
  { key: 'booked', label: 'Booked', width: 72, num: true, title: 'Meetings booked. Below: set rate (booked ÷ DM conversations)', shows: ['meetingsBooked', 'setRate'] },
  { key: 'held', label: 'Held', width: 72, num: true, title: 'Meetings held. Below: show rate (held ÷ (held + no-show))', shows: ['meetingsHeld', 'showRate'] },
  { key: 'dialsPerMeeting', label: 'Dials / mtg', width: 62, num: true, shows: ['dialsPerMeeting'] },
  { key: 'vmLive', label: 'VM live', width: 58, num: true, shows: ['vmLive'] },
  { key: 'vmDropped', label: 'VM drop', width: 58, num: true, shows: ['vmDropped'] },
  { key: 'talk', label: 'Talk min', width: 76, num: true, shows: ['talkMinutes'] },
];

/** Columns for the chosen grouping, in the page's order: week, campaign, setter (or the date range when ungrouped). */
export function funnelColumns(groupBy: ReadonlyArray<FunnelGroupKey>): FunnelColumn[] {
  const groups = (['week', 'campaign', 'user'] as const).filter((g) => groupBy.includes(g)).map((g) => FUNNEL_GROUP_COLUMNS[g]);
  return [...(groups.length ? groups : [FUNNEL_GROUP_COLUMNS.range]), ...FUNNEL_METRIC_COLUMNS];
}
